/**
 * VFXParticlesWebGL - WebGL shader-based particle rendering implementation.
 */

import * as THREE from 'three';

import { AppearanceMode, easeFunctionList, RenderModeEnum } from '../types.js';

import { glslEasings } from './VFXGLSLEasings.js';
import { VFXParticlesShared } from './VFXParticlesShared.js';

type VFXShaderUniforms = {
  alphaMap: { value: THREE.Texture | null };
  uAppearanceMode: { value: number };
  uEasingFunction: { value: number };
  uFadeAlpha: { value: THREE.Vector2 };
  uFadeSize: { value: THREE.Vector2 };
  uGravity: { value: THREE.Vector3 };
  uIntensity: { value: number };
  uStretchScale: { value: number };
  uTime: { value: number };
};

function buildDefines(renderMode: string, alphaMap?: THREE.Texture): Record<string, number> {
  const defines: Record<string, number> = {
    BILLBOARD_MODE: renderMode === RenderModeEnum.Billboard ? 1 : 0,
    MESH_MODE: renderMode === RenderModeEnum.Mesh ? 1 : 0,
    STRETCH_BILLBOARD_MODE: renderMode === RenderModeEnum.StretchBillboard ? 1 : 0,
  };

  if (alphaMap) {
    defines.USE_ALPHAMAP = 1;
  }

  return defines;
}

export class VFXParticlesWebGL extends VFXParticlesShared<THREE.ShaderMaterial> {
  protected override createMaterial(): THREE.ShaderMaterial {
    const material = new THREE.ShaderMaterial({
      defines: buildDefines(this.settings.renderMode, this.alphaMap),
      fog: true,
      fragmentShader: /* glsl */`
uniform float uIntensity;
uniform vec2 uFadeAlpha;
uniform sampler2D alphaMap;
uniform int uAppearanceMode;

${this.settings.shadingHooks.customUniforms ?? ''}

#include <fog_pars_fragment>

varying vec3 vColor;
varying vec3 vColorEnd;
varying float vProgress;
varying vec2 vUv;

${this.settings.shadingHooks.customVaryings ?? ''}

${this.settings.shadingHooks.fragmentBeforeMain ?? ''}

void main() {
  if (vProgress < 0.0 || vProgress > 1.0) {
    discard;
  }

  vec3 finalColor = mix(vColor, vColorEnd, vProgress) * uIntensity;
  float alpha = smoothstep(0.0, uFadeAlpha.x, vProgress) * smoothstep(1.01, uFadeAlpha.y, vProgress);

#ifdef USE_ALPHAMAP
  vec4 texSample = texture2D(alphaMap, vUv);
  finalColor = finalColor * texSample.rgb;
  alpha = texSample.a * alpha;
#else
  if (uAppearanceMode == ${AppearanceMode.Circular}) {
    if (distance(vUv, vec2(0.5, 0.5)) > 0.5) {
      discard;
    }
  }
#endif

  ${this.settings.shadingHooks.fragmentBeforeOutput ?? ''}
  gl_FragColor = vec4(finalColor, alpha);
  #include <fog_fragment>
}
`,
      uniforms: THREE.UniformsUtils.merge([
        {
          alphaMap: { value: this.alphaMap ?? null },
          uAppearanceMode: { value: this.settings.appearance },
          uEasingFunction: { value: easeFunctionList.indexOf(this.settings.easeFunction) },
          uFadeAlpha: { value: new THREE.Vector2(...this.settings.fadeAlpha) },
          uFadeSize: { value: new THREE.Vector2(...this.settings.fadeSize) },
          uGravity: { value: new THREE.Vector3(...this.settings.gravity) },
          uIntensity: { value: this.settings.intensity },
          uStretchScale: { value: this.settings.stretchScale },
          uTime: { value: 0 },
        } satisfies VFXShaderUniforms,
        THREE.UniformsLib.fog,
      ]),
      vertexShader: /* glsl */`
${glslEasings}

vec3 rotateXYZ(vec3 point, vec3 rotation) {
  float cx = cos(rotation.x);
  float sx = sin(rotation.x);
  float cy = cos(rotation.y);
  float sy = sin(rotation.y);
  float cz = cos(rotation.z);
  float sz = sin(rotation.z);

  vec3 rotatedY = vec3(
    point.x,
    point.y * cx - point.z * sx,
    point.y * sx + point.z * cx
  );
  vec3 rotatedZ = vec3(
    rotatedY.x * cy + rotatedY.z * sy,
    rotatedY.y,
    -rotatedY.x * sy + rotatedY.z * cy
  );

  return vec3(
    rotatedZ.x * cz - rotatedZ.y * sz,
    rotatedZ.x * sz + rotatedZ.y * cz,
    rotatedZ.z
  );
}

vec3 safeNormalize(vec3 value, vec3 fallbackValue) {
  float len = length(value);
  if (len < 0.00001) {
    return fallbackValue;
  }
  return value / len;
}

uniform float uTime;
uniform vec2 uFadeSize;
uniform vec3 uGravity;
uniform float uStretchScale;
uniform int uEasingFunction;

${this.settings.shadingHooks.customUniforms ?? ''}

#include <fog_pars_vertex>

varying vec2 vUv;
varying vec3 vColor;
varying vec3 vColorEnd;
varying float vProgress;

${this.settings.shadingHooks.customVaryings ?? ''}

attribute float instanceSpeed;
attribute vec3 instanceColor;
attribute vec3 instanceColorEnd;
attribute vec3 instanceDirection;
attribute vec2 instanceLifetime;
attribute vec3 instancePosition;
attribute vec3 instanceRotation;
attribute vec3 instanceRotationSpeed;
attribute vec3 instanceScale;

${this.settings.shadingHooks.vertexBeforeMain ?? ''}

void main() {
  float startTime = instanceLifetime.x;
  float duration = instanceLifetime.y;
  float age = instanceSpeed < 0.0
    ? duration - (uTime - startTime)
    : uTime - startTime;

  float rawProgress = age / duration;
  float clampedProgress = clamp(rawProgress, 0.0, 1.0);
  bool invalid = rawProgress < 0.0 || rawProgress >= 1.0;
  float easedProgress = applyEasing(clampedProgress, uEasingFunction);
  vProgress = invalid ? rawProgress : easedProgress;

  if (invalid) {
    gl_Position = vec4(9999.0, 9999.0, 9999.0, 1.0);
    return;
  }

  float scaleFade = smoothstep(0.0, uFadeSize.x, vProgress) * smoothstep(1.01, uFadeSize.y, vProgress);
  vec3 normalizedDirection = length(instanceDirection) > 0.0 ? normalize(instanceDirection) : vec3(0.0);
  vec3 gravityForce = 0.5 * uGravity * (age * age);
  float easedAge = vProgress * duration;
  vec3 offset = normalizedDirection * easedAge * instanceSpeed + gravityForce;
  vec3 worldBase = instancePosition + offset;
  vec3 dynRotation = instanceRotation + instanceRotationSpeed * age;
  vec3 worldPos;

#if BILLBOARD_MODE
  vec3 localZ = safeNormalize(cameraPosition - worldBase, vec3(0.0, 0.0, 1.0));
  vec3 localX = safeNormalize(cross(vec3(0.0, 1.0, 0.0), localZ), vec3(1.0, 0.0, 0.0));
  vec3 localY = cross(localZ, localX);

  float spin = dynRotation.z;
  float c = cos(spin);
  float s = sin(spin);
  vec2 scaled = vec2(
    position.x * instanceScale.x * scaleFade,
    position.y * instanceScale.y * scaleFade
  );
  vec2 spun = vec2(
    c * scaled.x - s * scaled.y,
    s * scaled.x + c * scaled.y
  );

  worldPos = worldBase + localX * spun.x + localY * spun.y;
#elif STRETCH_BILLBOARD_MODE
  vec3 worldVelocity = normalizedDirection * instanceSpeed + uGravity * age;
  float currentSpeed = length(worldVelocity);
  vec3 eyeVector = safeNormalize(cameraPosition - worldBase, vec3(0.0, 0.0, 1.0));
  vec3 tangent = safeNormalize(worldVelocity, vec3(0.0, 1.0, 0.0));
  vec3 projectedTangent = tangent - dot(tangent, eyeVector) * eyeVector;
  vec3 particlePlaneUp = length(projectedTangent) < 0.001
    ? tangent
    : normalize(projectedTangent);
  vec3 particlePlaneRight = safeNormalize(cross(particlePlaneUp, eyeVector), vec3(1.0, 0.0, 0.0));

  float width = instanceScale.x * scaleFade;
  float lengthScale = instanceScale.y * scaleFade * (1.0 + currentSpeed * uStretchScale);
  float spin = dynRotation.z;
  float c = cos(spin);
  float s = sin(spin);
  vec2 localSpun = vec2(
    c * position.x - s * position.y,
    s * position.x + c * position.y
  );

  worldPos = worldBase +
    particlePlaneRight * localSpun.x * width +
    particlePlaneUp * localSpun.y * lengthScale;
#else
  vec3 localScaled = vec3(
    position.x * instanceScale.x * scaleFade,
    position.y * instanceScale.y * scaleFade,
    position.z * instanceScale.z * scaleFade
  );
  vec3 rotated = rotateXYZ(localScaled, dynRotation);
  worldPos = worldBase + rotated;
#endif

  ${this.settings.shadingHooks.vertexBeforeOutput ?? ''}

  vec4 mvPosition = viewMatrix * vec4( worldPos, 1.0 );
  #include <fog_vertex>

  gl_Position = projectionMatrix * mvPosition;
  vUv = uv;
  vColor = instanceColor;
  vColorEnd = instanceColorEnd;
}
`,
    });

    return material;
  }

  protected override updateMaterialUniforms(elapsedTime: number): void {
    const uniforms = this.material.uniforms as VFXShaderUniforms;
    uniforms.uTime.value = elapsedTime;
    uniforms.uIntensity.value = this.settings.intensity;
    uniforms.uStretchScale.value = this.settings.stretchScale;
    uniforms.uFadeSize.value.set(...this.settings.fadeSize);
    uniforms.uFadeAlpha.value.set(...this.settings.fadeAlpha);
    uniforms.uGravity.value.set(...this.settings.gravity);
    uniforms.uAppearanceMode.value = this.settings.appearance;
    uniforms.uEasingFunction.value = easeFunctionList.indexOf(this.settings.easeFunction);
  }
}
